import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';

import {
    Card as ApiCard,
    updateCard,
    getCommentList,
    createComment,
    updateComment,
    deleteComment,
    Comment as ApiComment,
    uploadAttachment,
    downloadAttachment,
    deleteAttachment,
    getCardById,
    Label as ApiLabel, ReportLevel as ApiReportLevel, getCardsByStatus,
    createCard, getProjectById
} from '../../api';
import {baseURL} from "../../lib/apiClient";
import useUserStore from '../../store/userStore';
import Modal from '../common/Modal';
import Button from '../common/Button';
import Input from '../common/Input';
import {useNavigate, useLocation} from "react-router-dom";

// ✨ [스타일] CopyButton
const CopyButton = styled(Button)`
  background-color: var(--text-normal);
  color: white;
  &:hover {
    background-color: var(--text-dark);
  }
`;

// ✨ [스타일] 탭 헤더
const TabHeader = styled.div`
  display: flex;
  border-bottom: 1px solid var(--border-color);
  margin-bottom: 1.5rem;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  background-color: var(--bg-modal);
  z-index: 5;
  padding-top: 0.5rem;
`;

const TabButton = styled.button<{ isActive: boolean }>`
  flex: 1;
  padding: 0.75rem;
  background: none;
  border: none;
  border-bottom: 2px solid ${props => props.isActive ? 'var(--primary-accent)' : 'transparent'};
  color: ${props => props.isActive ? 'var(--text-bright)' : 'var(--text-normal)'};
  font-weight: ${props => props.isActive ? 'bold' : 'normal'};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    color: var(--text-bright);
    background-color: var(--bg-input);
  }
`;

// ✨ [스타일] Todo 섹션
const TodoSection = styled.div`
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TodoTableContainer = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background-color: var(--bg-input);
  overflow: hidden;
`;

const TodoRowItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  border-bottom: 1px solid var(--border-color);
  gap: 0.5rem;
  background-color: var(--bg-input);
  
  &:last-child {
    border-bottom: none;
  }
  &.dragging {
    opacity: 0.5;
    background-color: var(--bg-card);
  }
  &:hover {
    background-color: rgba(255, 255, 255, 0.02);
  }
`;

const DragHandle = styled.div`
  color: var(--text-dark);
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.2rem;
  touch-action: none; /* 모바일에서 드래그 시 스크롤 방지 */
  &:hover { color: var(--text-bright); }
  &:active { cursor: grabbing; }
  svg { width: 16px; height: 16px; }
`;

const TodoCheckbox = styled.div<{ checked: boolean, readOnly?: boolean }>`
  width: 18px;
  height: 18px;
  border: 2px solid ${props => props.checked ? 'var(--text-dark)' : 'var(--text-normal)'};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.readOnly ? 'default' : 'pointer'};
  background-color: ${props => props.checked ? 'var(--text-dark)' : 'transparent'};
  flex-shrink: 0;
  transition: all 0.2s;
  opacity: ${props => props.readOnly ? 0.7 : 1};

  &:hover {
    border-color: ${props => props.readOnly ? '' : 'var(--primary-accent)'};
  }

  svg {
    width: 12px;
    height: 12px;
    stroke: var(--bg-input);
    stroke-width: 3;
  }
`;

const TodoInput = styled.input<{ completed: boolean }>`
  background: transparent;
  border: none;
  color: ${props => props.completed ? 'var(--text-dark)' : 'var(--text-bright)'};
  font-size: 0.95rem;
  width: 100%;
  outline: none;
  text-decoration: ${props => props.completed ? 'line-through' : 'none'};
  font-style: ${props => props.completed ? 'italic' : 'normal'};
  
  &:read-only {
    cursor: default;
  }
  
  &::placeholder {
    color: var(--text-dark);
    font-style: italic;
  }
`;

const TodoDeleteBtn = styled.button`
  background: none;
  border: none;
  color: var(--text-dark);
  cursor: pointer;
  padding: 0.2rem;
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s;

  ${TodoRowItem}:hover & {
    opacity: 1;
  }
  &:hover { color: var(--danger-accent); }
`;

const AddTodoRowButton = styled.button`
  width: 100%;
  padding: 0.5rem;
  background: none;
  border: none;
  border-top: 1px solid var(--border-color);
  color: var(--text-normal);
  cursor: pointer;
  text-align: left;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    color: var(--text-bright);
  }
`;

const EmptyTodoPlaceholder = styled.div`
  padding: 1rem;
  text-align: center;
  border: 1px dashed var(--border-color);
  border-radius: 4px;
  color: var(--text-normal);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    border-color: var(--primary-accent);
    color: var(--primary-accent);
    background-color: rgba(97, 175, 239, 0.05);
  }
`;

interface ToDoDto {
    isDone: boolean;
    content: string;
    priority: number;
}

interface TodoItem {
    id: string;
    content: string;
    completed: boolean;
    priority: number;
}

const StateSelect = styled.select<{ bgColor: string }>`
  padding: 0.2rem 1.0rem 0.2rem 0.8rem;
  border-radius: 12px;
  border: 1px solid transparent;
  background-color: ${props => props.bgColor};
  color: #000;
  font-weight: bold;
  font-size: 0.85rem;
  outline: none;
  cursor: pointer;
  appearance: auto;

  &:focus {
    box-shadow: 0 0 0 2px var(--border-color);
  }
  &:disabled {
    cursor: default;
    opacity: 0.8;
  }

  option {
    background-color: var(--bg-input);
    color: var(--text-bright);
    font-weight: normal;
  }
`;

const formatMs = (ms?: number) => {
    if (ms === undefined || ms === null) return '-';
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;

    const parts = [];
    if (h > 0) parts.push(`${h}시간`);
    if (m > 0) parts.push(`${m}분`);
    if (s > 0 || parts.length === 0) parts.push(`${s}초`);
    return parts.join(' ');
};

const formatDateTime = (dt?: string) => {
    if (!dt) return '-';
    return dt;
};

const getStateColor = (state?: string) => {
    switch (state) {
        case 'IDLE': return '#FFD54F';
        case 'FOLLOWUP': return '#BA68C8';
        case 'COMPLETED': return '#81C784';
        case 'PROCESS': return '#64B5F6';
        default: return 'var(--text-dark)';
    }
};

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 80vh;
  max-height: 90vh;
  width: 100%;

  @media (max-width: 768px) {
    height: 90vh;
    width: 100%;
  }
`;

const ModalTopHeader = styled.div`
  border-radius: 20px 20px 0 0;
  background-color: var(--bg-header);
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  min-height: fit-content;
  flex-shrink: 0;
`;

const HeaderTitle = styled.h2`
  color: var(--text-bright);
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0;
  flex: 1;
  min-width: 0;
`;

const HeaderButtonContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0;
  width: 100%;
`;

const CardTitleInput = styled.textarea.attrs({ className: 'card-title-input' })`
  font-size: 1.5rem;
  font-weight: bold;
  border: none;
  background: transparent;
  width: 100%;
  padding: 0.25rem;
  border-radius: 4px;
  color: var(--text-bright);
  resize: none;
  overflow-y: auto;
  min-height: 2rem;
  max-height: 120px;
  line-height: 1.2;
  font-family: inherit;
  margin-top: 0.25rem;

  &:focus {
    background-color: var(--bg-input);
    box-shadow: inset 0 0 0 2px var(--primary-accent);
  }
  flex-grow: 1;
`;

const HeaderCloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-normal);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { color: var(--text-bright); }
  svg { width: 20px; height: 20px; }
`;

const CommentToggleButton = styled.button`
  display: none;
  @media (max-width: 768px) {
    display: block;
    top: 1.25rem;
    right: 1.25rem;
    z-index: 10;
    background: transparent;
    color: var(--text-bright);
    border-radius: 4px;
    width: 32px;
    height: 32px;
    cursor: pointer;
    svg { width: 20px; height: 20px; }
  }
`;

const ModalBody = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  background-color: var(--bg-modal);
  overflow: hidden;
  border-bottom: 1px solid var(--border-color);
`;

const ModalGrid = styled.div<{ showComments: boolean }>`
  display: grid;
  grid-template-columns: 2.5fr 1fr;
  gap: 0;
  padding: 1.5rem;
  height: 100%;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    right: 28.5%;
    width: 1px;
    background-color: var(--border-color);
  }

  @media (max-width: 768px) {
    display: flex;
    width: 200%;
    transition: transform 0.3s ease-in-out;
    transform: translateX(${props => props.showComments ? '-50%' : '0'});
    padding: 1rem;
    &::after { display: none; }
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-right: 1.5rem;
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background-color: var(--text-dark);
    border-radius: 4px;
    border: 2px solid var(--bg-modal);
  }
  @media (max-width: 768px) {
    width: 50%;
    padding-right: 1rem;
  }
`;

const SideContent = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding-left: 1.5rem;
  @media (max-width: 768px) {
    width: 50%;
    padding-left: 1rem;
  }
`;

const EditorContainer = styled.div`
  position: relative;
`;

const TiptapWrapper = styled.div<{ isExpanded: boolean, isEditable: boolean }>`
  border: 1px solid var(--border-color);
  border-radius: 4px;
  transition: border-color 0.2s ease-in-out;
  cursor: ${props => props.isEditable ? 'text' : 'default'};
  display: flex;
  flex-direction: column;
  background-color: var(--bg-modal);

  &:hover {
    border-color: ${props => !props.isEditable ? 'var(--text-normal)' : 'var(--border-color)'};
  }

  .tiptap {
    padding: 1rem;
    color: var(--text-bright);
    max-height: ${props => props.isExpanded ? 'none' : '250px'};
    min-height: 300px;
    overflow: visible;
    flex-grow: 1;

    > p:first-child { margin-top: 0; }
    &:focus { outline: none; }

    table {
      width: 100%; border-collapse: collapse; margin: 1rem 0;
      td, th {
        border: 1px solid var(--border-color); padding: 0.5rem;
        min-width: 1em; position: relative;
      }
      th {
        font-weight: bold; background-color: var(--bg-input);
        text-align: left;
      }
    }
    img {
      max-width: 100%; height: auto; cursor: pointer;
      &.ProseMirror-selectednode { outline: 3px solid var(--primary-accent); }
    }
    ul, ol { padding-left: 1.5rem; }
  }
`;

const MenuBarContainer = styled.div`
  background-color: var(--bg-input);
  padding: 0.5rem;
  border-bottom: 1px solid var(--border-color);
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-wrap: wrap;

  button {
    background: transparent; border: none; color: var(--text-normal);
    padding: 0.2rem 0.4rem; margin-right: 0.2rem; border-radius: 3px;
    cursor: pointer;
    &.is-active { background-color: var(--primary-accent); color: white; }
    &:hover { background-color: var(--border-color); }
  }
`;

const CommentList = styled.div`
  flex-grow: 1; overflow-y: auto; margin-top: 1rem; display: flex;
  flex-direction: column; gap: 1rem; padding-right: 0.5rem;
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background-color: var(--text-dark); border-radius: 4px;
    border: 2px solid var(--bg-modal);
  }
`;

const CommentItem = styled.div` background-color: #202326; padding: 0.75rem; border-radius: 4px; `;
const CommentHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  margin-bottom: 0.5rem; color: var(--text-dark); font-size: 0.8rem;
`;
const CommentAuthor = styled.span` font-weight: bold; color: var(--text-normal); `;
const CommentContent = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  img { max-width: 100%; height: auto; }
`;
const CommentActions = styled.div`
  display: flex; gap: 0.5rem; margin-top: 0.5rem;
  button {
    background: none; border: none; color: var(--text-dark); cursor: pointer;
    text-decoration: underline; font-size: 0.8rem; padding: 0;
    &:hover { color: var(--primary-accent); }
  }
`;

// ✨ [수정] readOnly일 때 버튼 정렬 수정
const ButtonGroup = styled.div<{ readOnly?: boolean }>`
  border-radius: 20px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 1.5rem 1.5rem 1.5rem;
  gap: 0.5rem;
  background-color: var(--bg-modal);
  padding-top: 20px;
  flex-shrink: 0;
  
  ${props => props.readOnly && `
    justify-content: center;
  `}
`;

const DeleteButton = styled(Button)`
  background-color: var(--danger-accent);
  &:hover { background-color: var(--danger-accent-hover); }
`;

const AttachmentList = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const AttachmentItem = styled.div`
  background-color: var(--bg-input);
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const AttachmentName = styled.button`
  font-weight: normal;
  font-size: 0.9rem;
  color: var(--text-normal);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
    color: var(--text-bright);
  }
`;
const AttachmentActions = styled.div`
  display: flex;
  gap: 0.5rem;
  button {
    background: none; border: none; color: var(--text-dark); cursor: pointer;
    text-decoration: underline; font-size: 0.8rem; padding: 0;
    &:hover { color: var(--primary-accent); }
  }
`;
const AttachmentSection = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;
const AddAttachmentButton = styled(Button)`
  align-self: flex-start;
  padding: 0.4rem 0.8rem;
  font-size: 0.9rem;
  background-color: var(--bg-list);
  &:hover { background-color: var(--border-color); }
`;
const LabelGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;
const LabelPill = styled.div<{ color: string }>`
  background-color: ${props => props.color};
  color: white;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const AddItemForm = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;
const ColorInput = styled.input`
  width: 40px;
  height: 38px;
  padding: 0;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  cursor: pointer;
`;
const SmallDeleteButton = styled.button`
  background: none; border: none; color: var(--text-dark); cursor: pointer;
  &:hover { color: var(--danger-accent); }
`;
const DateInput = styled(Input)`
  margin-top: 0.5rem;
  &:disabled { opacity: 0.8; cursor: default; }
`;
const MetadataContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1.5rem 0;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--border-color);
`;
const MetadataGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const DateGroup = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-end;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;
const DateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
`;
const DateLabel = styled.label`
  font-size: 0.8rem;
  color: var(--text-normal);
`;
const Tilde = styled.span`
  color: var(--text-normal);
  font-size: 1.5rem;
  padding-bottom: 0.3rem;
  @media (max-width: 768px) { display: none; }
`;
const ReportLevelLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;
const ReportLevelPill = styled.div`
  background-color: var(--bg-input);
  border: 1px solid var(--border-color);
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const ArrowSpan = styled.span`
  color: var(--text-normal);
  font-weight: bold;
`;

const TimeInfoGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    background-color: var(--bg-input);
    padding: 0.75rem;
    border-radius: 4px;
    margin-top: 0.5rem;
`;
const TimeInfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.9rem;
    color: var(--text-normal);
`;
const TimeLabel = styled.span`
    font-weight: bold;
    color: var(--text-bright);
`;
const TimeValue = styled.span`
    color: var(--text-bright);
`;

const CommentTextarea = styled.textarea`
  font-size: 1rem;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 0.5rem;
  outline: none;
  width: 100%;
  background-color: var(--bg-input);
  color: var(--text-bright);
  resize: vertical;
  min-height: 60px;
  &:focus { border-color: var(--primary-accent); }
`;
const CompleteCheckbox = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid var(--text-dark);
  border-radius: 50%;
  margin-top: 0.5rem;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease-in-out;
  &:hover { border-color: #61afef; }
  &.completed {
    background-color: #98c379;
    border-color: #98c379;
  }
`;
const CheckIcon = styled.svg`
  width: 16px;
  height: 16px;
  stroke: white;
  stroke-width: 2.5;
`;
const GoToBoardContainer = styled.div`
  margin: 0;
  display: flex;
  align-items: center;
  margin-right: auto;
`;
const StartWorkButton = styled(Button)<{ isWorking: boolean }>`
  background-color: ${props => props.isWorking ? '#e06c75' : '#98c379'};
  border-radius: 20px;
  padding: 0.4rem 1rem;
  &:hover {
    background-color: ${props => props.isWorking ? '#be5046' : '#7a9f60'};
  }
`;
const WarningContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const WarningOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 1;
`;
const AlertModal = styled(Modal)<{ onClick: (e: React.MouseEvent) => void }>`
  max-width: 400px;
  margin: 0 auto;
  z-index: 2;
`;
const AlertContent = styled.div`
  padding: 1.5rem;
  color: var(--text-bright);
`;
const CardList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 1rem 0;
`;
const CardItem = styled.div`
  background-color: var(--bg-card);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  color: var(--text-normal);
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  margin: 0.5rem 0;
  transition: all 0.2s ease-in-out;
  &:hover { background-color: var(--border-color); }
`;
const CardTitle = styled.div`
  flex-grow: 1;
  word-break: break-word;
`;
const CardStatus = styled.div`
  font-size: 0.8rem;
  color: var(--text-dark);
`;

interface WarningModalProps {
    cards: ApiCard[];
    onClose: () => void;
    onConfirm: () => void;
    currentCardId: string;
}
interface CardDetailModalProps {
    card: ApiCard;
    onClose: () => void;
    onUpdate: (updatedCard: ApiCard) => void;
    onDelete: (cardId: string) => void;
    onToggleComplete: () => void;
    fromCalendar?: boolean;
    fromBoard?: boolean;
    readOnly?: boolean; // ✨ readOnly prop 추가
    onRemoveFromProject?: () => void;
    isAdminEdit?: boolean
}

const WarningModal: React.FC<WarningModalProps> = ({ cards, onClose, onConfirm }) => {
    const navigate = useNavigate();
    const handleClickOutside = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.warning-modal-content')) {
            onClose();
        }
    };
    const handleCardClick = (card: ApiCard) => {
        onClose();
        navigate(`/board/${card.boardId}/card/${card.id}`);
    };
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    return (
        <WarningContainer>
            <AlertModal visible={true} onClose={onClose} onClick={handleClickOutside}>
                <div className="modal-content warning-modal-content">
                    <ModalTopHeader>
                        <HeaderTitle>Warning</HeaderTitle>
                        <HeaderCloseButton onClick={onClose}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </HeaderCloseButton>
                    </ModalTopHeader>
                    <AlertContent>
                        <p>현재 진행 중인 다른 카드가 있습니다:</p>
                        <CardList>
                            {cards.map(card => (
                                <CardItem key={card.id} onClick={() => handleCardClick(card)}>
                                    <CardTitle>{card.title}</CardTitle>
                                    <CardStatus>진행중</CardStatus>
                                </CardItem>
                            ))}
                        </CardList>
                        <p>계속 진행하시겠습니까?</p>
                        <ButtonGroup>
                            <Button onClick={onClose}>취소</Button>
                            <Button onClick={onConfirm}>계속</Button>
                        </ButtonGroup>
                    </AlertContent>
                </div>
            </AlertModal>
            <WarningOverlay onClick={onClose} />
        </WarningContainer>
    );
};

// --- Tiptap 메뉴바 컴포넌트 ---
const MenuBar: React.FC<{ editor: Editor | null, onImageUpload: () => void }> = ({ editor, onImageUpload }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    if (!editor) return null;
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const base64String = e.target?.result as string;
                if (base64String) {
                    editor.chain().focus().setImage({ src: base64String }).run();
                }
            };
            reader.readAsDataURL(file);
        }
    };
    const addTable = () => {
        const rows = parseInt(window.prompt('Enter number of rows', '3') || '0', 10);
        const cols = parseInt(window.prompt('Enter number of columns', '3') || '0', 10);
        if (rows > 0 && cols > 0) {
            editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
        }
    };

    return (
        <>
            <MenuBarContainer>
                <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''}>Bold</button>
                <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''}>Italic</button>
                <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active' : ''}>List</button>
                <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'is-active' : ''}>1. List</button>
                <button type="button" onClick={onImageUpload}>Image</button>
                <button type="button" onClick={addTable}>Table</button>

                {editor.isActive('table') && (
                    <>
                        <button type="button" onClick={() => editor.chain().focus().addColumnAfter().run()}>Add Col</button>
                        <button type="button" onClick={() => editor.chain().focus().addRowAfter().run()}>Add Row</button>
                        <button type="button" onClick={() => editor.chain().focus().deleteColumn().run()}>Del Col</button>
                        <button type="button" onClick={() => editor.chain().focus().deleteRow().run()}>Del Row</button>
                        <button type="button" onClick={() => editor.chain().focus().deleteTable().run()}>Del Table</button>
                    </>
                )}
            </MenuBarContainer>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} accept="image/*"/>
        </>
    );
};

const CardDetailModal: React.FC<CardDetailModalProps> = ({ isAdminEdit, card, onClose, onUpdate, onDelete, onToggleComplete, fromCalendar, fromBoard, readOnly: readOnlyProp = false, onRemoveFromProject }) => {
    const { user } = useUserStore();
    // 고칠 수 있는 사람은 서버와 같다: 카드 주인은 전부, 담당자는 진행(상태 · 할 일 · 시작/멈춤 · 완료)만.
    // 프로젝트 주인이라도 둘 다 아니면 고치지 못한다(서버가 거절). 댓글은 예전처럼 readOnlyProp 만 본다.
    const isCardOwner = !card.userId || card.userId === user?.id;
    const currentAssignee = card.assigneeId || card.userId || '';
    const readOnly = readOnlyProp || !(isCardOwner || currentAssignee === user?.id);
    const contentLocked = readOnly || !isCardOwner;
    const navigate = useNavigate();
    const location = useLocation();
    const [title, setTitle] = useState(card.title);
    const [description, setDescription] = useState(card.description || '');
    const [comments, setComments] = useState<ApiComment[]>([]);
    const [newComment, setNewComment] = useState('');
    const [editingComment, setEditingComment] = useState<ApiComment | null>(null);
    const initialTitle = useRef(card.title);
    const initialDescription = useRef(card.description || '');
    const fileUploadRef = useRef<HTMLInputElement>(null);
    const imageUploadRef = useRef<HTMLInputElement>(null);
    const editorContentRef = useRef<HTMLDivElement>(null);
    const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);
    const [, setForceUpdate] = useState(0);
    const [labels, setLabels] = useState<ApiLabel[]>([]);
    const [reportLevels, setReportLevels] = useState<ApiReportLevel[]>([]);
    const [startDate, setStartDate] = useState('');
    const [dueDate, setDueDate] = useState('');

    const [newLabelName, setNewLabelName] = useState('');
    const [newLabelColor, setNewLabelColor] = useState('#61afef');
    const [newReportLevelName, setNewReportLevelName] = useState('');

    const initialLabels = useRef<ApiLabel[]>([]);
    const initialReportLevels = useRef<ApiReportLevel[]>([]);
    const initialStartDate = useRef('');
    const initialDueDate = useRef('');

    const [isDescriptionEditable, setIsDescriptionEditable] = useState(false);
    const mainContentRef = useRef<HTMLDivElement>(null);

    const [showCommentsOnMobile, setShowCommentsOnMobile] = useState(false);

    const [showWarning, setShowWarning] = useState(false);
    const [inProgressCards, setInProgressCards] = useState<ApiCard[]>([]);
    const pendingStatusUpdate = useRef<string | null>(null);

    const [cardState, setCardState] = useState(card.state || 'IDLE');
    const initialCardState = useRef(card.state || 'IDLE');

    const [todos, setTodos] = useState<TodoItem[]>([]);
    const initialTodos = useRef<ToDoDto[]>([]);

    const searchParams = new URLSearchParams(location.search);
    const activeTab = searchParams.get('tab') || 'general';

    const dragItem = useRef<number | null>(null);
    const dragOverItem = useRef<number | null>(null);

    const [projectName, setProjectName] = useState<string>('');
    const [predecessors, setPredecessors] = useState<{ id: string, title: string, taskCode?: string }[]>([]);

    const [isSaving, setIsSaving] = useState(false); // 저장 중 상태 표시용

    const [projectMemberIds, setProjectMemberIds] = useState<string[]>([]);

    // 1. 수동 저장 전용 핸들러 추가
    const handleManualSave = async () => {
        setIsSaving(true);
        const updated = await handleSaveCardDetails();
        if (updated) {
            onUpdate(updated); // 부모 컴포넌트 UI 갱신
        }
        setIsSaving(false);
    };

    // ✨ [신규] 터치 시작 핸들러
    const handleTouchStart = (index: number) => {
        if (readOnly) return;
        dragItem.current = index;
    };

    // ✨ [신규] 터치 이동 핸들러 (현재 손가락이 위치한 아이템의 인덱스 파악)
    const handleTouchMove = (e: React.TouchEvent) => {
        if (readOnly || dragItem.current === null) return;

        const touch = e.touches[0];
        // 터치 좌표에 있는 요소를 찾음
        const target = document.elementFromPoint(touch.clientX, touch.clientY);
        const row = target?.closest('.todo-row-item') as HTMLElement;

        if (row) {
            const index = parseInt(row.dataset.index || '-1');
            if (index !== -1 && index !== dragOverItem.current) {
                dragOverItem.current = index;
            }
        }
    };

    // ✨ [신규] 터치 종료 핸들러
    const handleTouchEnd = () => {
        if (readOnly) return;
        handleSort(); // 기존 정렬 로직 재사용
    };

    // ✨ [수정] 상위에서 내려준 onToggleComplete를 감싸서 에러 처리를 할 수도 있지만,
    // 보통 이 함수를 호출하는 쪽(부모 컴포넌트)에서 에러를 처리하거나,
    // 여기서 직접 API를 호출하고 에러를 잡아야 합니다.
    // 현재 구조상 handleClose(저장)나 체크박스 클릭 시 동작을 확인해야 합니다.

    // 이 파일의 CompleteCheckbox onClick 이벤트는 props인 onToggleComplete를 직접 호출합니다.
    // 따라서 부모 컴포넌트들(AllTasksPage, BoardPage 등)의 핸들러를 수정하는 것이 맞습니다.
    // 하지만, 편의를 위해 여기서 래핑 함수를 만들어 처리할 수도 있습니다.

    // ✨ [신규] 안전한 토글 핸들러
    const handleSafeToggleComplete = async () => {
        if (readOnly) return;
        try {
            // onToggleComplete가 Promise를 반환한다고 가정 (async 함수라면)
            await onToggleComplete();
        } catch (error: any) {
            console.error("Error toggling complete", error);
            const message = error.response?.data?.data || "완료 처리 실패";
            alert(message);
        }
    };

    const handleSort = () => {
        if (dragItem.current === null || dragOverItem.current === null || dragItem.current === dragOverItem.current) {
            dragItem.current = null;
            dragOverItem.current = null;
            return;
        }
        const _todos = [...todos];
        const draggedItemContent = _todos.splice(dragItem.current, 1)[0];
        _todos.splice(dragOverItem.current, 0, draggedItemContent);
        dragItem.current = null;
        dragOverItem.current = null;
        setTodos(_todos);
        handleSaveCardDetails({ todoList: getApiTodoList(_todos) });
    };

    const handleTabChange = (newTab: string) => {
        const newParams = new URLSearchParams(location.search);
        newParams.set('tab', newTab);
        navigate({ pathname: location.pathname, search: newParams.toString() }, { replace: true });
    };

    const handleAddTodo = () => {
        const newTodo: TodoItem = {
            id: `new-${Date.now()}`,
            content: '',
            completed: false,
            priority: todos.length
        };
        setTodos([...todos, newTodo]);
    };

    const handleToggleTodo = async (id: string) => {
        const updatedTodos = todos.map(todo =>
            todo.id === id ? { ...todo, completed: !todo.completed } : todo
        );
        setTodos(updatedTodos);
        await handleSaveCardDetails({ todoList: getApiTodoList(updatedTodos) });
    };

    const handleChangeTodo = (id: string, value: string) => {
        setTodos(todos.map(todo =>
            todo.id === id ? { ...todo, content: value } : todo
        ));
    };

    const handleBlurTodo = async () => {
        await handleSaveCardDetails();
    };

    const handleDeleteTodo = async (id: string) => {
        const updatedTodos = todos.filter(todo => todo.id !== id);
        setTodos(updatedTodos);
        await handleSaveCardDetails({ todoList: getApiTodoList(updatedTodos) });
    };

    const handleToggleWorkStatus = async () => {
        try {
            if (card.isEmergency && card.status === 'IN_PROGRESS') {
                alert("긴급 업무는 상단 헤더의 '긴급 종료' 기능을 이용해 주세요.");
                return;
            }
            const newStatus = card.status === 'IN_PROGRESS' ? 'TODO' : 'IN_PROGRESS';
            if (newStatus === 'IN_PROGRESS') {
                const response = await getCardsByStatus({ status: 'IN_PROGRESS' });
                const currentInProgressCards = response.data?._embedded?.getList || [];
                // '한 사람 하나 진행'은 실제로 맡은 사람(담당자) 기준이다. 내가 맡은 카드끼리만 견준다.
                const otherCards = currentAssignee === user?.id
                    ? currentInProgressCards.filter(c => c.id !== card.id && (c.assigneeId || c.userId) === currentAssignee)
                    : [];
                if (otherCards.length > 0) {
                    setInProgressCards(otherCards);
                    setShowWarning(true);
                    pendingStatusUpdate.current = newStatus;
                    return;
                }
            }
            await updateCardStatus(newStatus);
            window.dispatchEvent(new Event('cardStatusChanged'));
        } catch (error) {
            console.error('Failed to update card status', error);
            alert('카드 상태 변경에 실패했습니다.');
        }
    };

    const updateCardStatus = async (newStatus: string) => {
        try {
            if (newStatus === 'IN_PROGRESS' && inProgressCards.length > 0) {
                for (const inProgressCard of inProgressCards) {
                    await updateCard({ id: inProgressCard.id, status: 'TODO' });
                }
            }
            const updatedCard = await updateCard({ id: card.id, status: newStatus });
            onUpdate(updatedCard.data);
            pendingStatusUpdate.current = null;
        } catch (error) {
            console.error('Failed to update card status', error);
            alert('카드 상태 변경에 실패했습니다.');
        }
    };

    const formatDateForInput = (dateString: string | null) => {
        if (!dateString) return '';
        return dateString.replace(' ', 'T').substring(0, 16);
    };

    // ✨ Editor Config: readOnly 적용
    const editor = useEditor({
        extensions: [
            StarterKit,
            Image.configure({ inline: true, allowBase64: true }),
            Table.configure({ resizable: true }),
            TableRow, TableHeader, TableCell,
        ],
        content: description,
        editable: !contentLocked && isDescriptionEditable, // ✨ readOnly면 편집 불가
        onUpdate: ({ editor }) => {
            if(!contentLocked) setDescription(editor.getHTML()); // ✨ readOnly면 업데이트 방지
            if (mainContentRef.current && isDescriptionEditable) {
                mainContentRef.current.scrollTop = mainContentRef.current.scrollHeight;
            }
        },
        onSelectionUpdate: () => setForceUpdate(val => val + 1),
    });

    useEffect(() => {
        const titleInput = document.querySelector<HTMLTextAreaElement>('.card-title-input');
        if (titleInput) {
            titleInput.style.height = 'auto';
            titleInput.style.height = `${titleInput.scrollHeight}px`;
        }
    }, [title, card.title]);

    // ✨ readOnly 변경 시 에디터 상태 업데이트
    useEffect(() => {
        if (editor) {
            editor.setEditable(!contentLocked && isDescriptionEditable);
        }
    }, [isDescriptionEditable, editor, contentLocked]);

    const handleDescriptionClick = () => {
        if (!isDescriptionOverflowing && !contentLocked) {
            setIsDescriptionEditable(true);
            if(editor) editor.setEditable(true);
        }
    };

    const fetchComments = useCallback(async () => {
        try {
            const response = await getCommentList(card.id);
            setComments(response.data._embedded?.getList || []);
        } catch (error) { console.error('Failed to fetch comments', error); }
    }, [card.id]);

    const handleAddLabel = () => {
        if (!newLabelName.trim()) return;
        setLabels([...labels, { name: newLabelName, color: newLabelColor }]);
        setNewLabelName('');
    };
    const handleDeleteLabel = (name: string) => {
        setLabels(labels.filter(label => label.name !== name));
    };

    const handleAddReportLevel = () => {
        if (!newReportLevelName.trim()) return;
        const newLevel = { name: newReportLevelName, priority: reportLevels.length };
        setReportLevels([...reportLevels, newLevel].sort((a, b) => a.priority - b.priority));
        setNewReportLevelName('');
    };
    const handleDeleteReportLevel = (name: string) => {
        const updatedLevels = reportLevels
            .filter(level => level.name !== name)
            .map((level, index) => ({ ...level, priority: index }));
        setReportLevels(updatedLevels);
    };

    useEffect(() => {
        if (editor) {
            const currentDescription = card.description || '';
            if (editor.getHTML() !== currentDescription) {
                editor.commands.setContent(currentDescription, { emitUpdate: false });
            }
        }
    }, [card.description, editor]);

    useEffect(() => {
        setTitle(card.title);
        initialTitle.current = card.title;
        initialDescription.current = card.description || '';
        fetchComments().then(r => r);

        const currentLabels = card.labels || [];
        const currentReportLevels = card.reportLevels || [];
        const currentStartDate = formatDateForInput(card.startDate);
        const currentDueDate = formatDateForInput(card.dueDate);

        setLabels(currentLabels);
        setReportLevels(currentReportLevels);
        setStartDate(currentStartDate);
        setDueDate(currentDueDate);

        const currentState = card.state || 'IDLE';
        setCardState(currentState);
        initialCardState.current = currentState;

        const apiTodoList: ToDoDto[] = (card as any).todoList || [];
        const sortedApiTodos = [...apiTodoList].sort((a, b) => a.priority - b.priority);

        const mappedTodos = sortedApiTodos.map((item, index) => ({
            id: `todo-${Date.now()}-${index}`,
            content: item.content,
            completed: item.isDone,
            priority: item.priority
        }));
        setTodos(mappedTodos);
        initialTodos.current = sortedApiTodos;

        initialLabels.current = currentLabels;
        initialReportLevels.current = currentReportLevels;
        initialStartDate.current = currentStartDate;
        initialDueDate.current = currentDueDate;
    }, [card, fetchComments]);

    const getApiTodoList = (currentTodos: TodoItem[]): ToDoDto[] => {
        return currentTodos.map((t, index) => ({
            isDone: t.completed,
            content: t.content,
            priority: index
        }));
    };

    useEffect(() => {
        const checkOverflow = () => {
            const element = editorContentRef.current?.querySelector('.tiptap');
            if (element) {
                setIsDescriptionOverflowing(element.scrollHeight > element.clientHeight);
            }
        };
        editor?.on('transaction', checkOverflow);
        const timer = setTimeout(checkOverflow, 100);
        return () => {
            editor?.off('transaction', checkOverflow);
            clearTimeout(timer);
        };
    }, [editor, card.description]);

    // ✨ [Req 4] 프로젝트명과 선행 업무 이름 가져오기
    useEffect(() => {
        const fetchAdditionalInfo = async () => {
            // 1. 프로젝트 이름 (boardId가 프로젝트 ID라고 가정)
            if (card.projectId) {
                try {
                    const pRes = await getProjectById(card.projectId);
                    setProjectName(pRes.data.title);
                    setProjectMemberIds([pRes.data.ownerId, ...(pRes.data.memberIds || [])]);
                } catch {
                    setProjectName('Unknown Project');
                    setProjectMemberIds([]);
                }
            } else {
                setProjectName(''); // ✨ [수정] 프로젝트가 없으면 초기화
                setProjectMemberIds([]);
            }

            // 2. 선행 업무 이름들
            if (card.predecessorIds && card.predecessorIds.length > 0) {
                try {
                    // 성능 최적화를 위해선 id list로 한 번에 조회하는 API가 좋지만,
                    // 현재는 개별 조회 혹은 상위에서 넘겨받아야 함. 여기선 Promise.all 사용
                    const promises = card.predecessorIds.map(id => getCardById(id));
                    const responses = await Promise.all(promises);
                    const predData = responses.map(r => ({ id: r.data.id, title: r.data.title, taskCode: r.data.taskCode }));
                    setPredecessors(predData);
                } catch { setPredecessors([]); }
            } else {
                setPredecessors([]); // ✨ [수정] 선행 업무가 없으면 빈 배열로 초기화 (이게 없어서 잔상이 남았음)
            }
        };
        fetchAdditionalInfo();
    }, [card.boardId, card.predecessorIds, card.projectId]); // card 객체 변경 시 재조회

    // 담당자 고르기: 만든 사람을 고르면 '담당자 없음(= 만든 사람)'으로 보낸다.
    const assigneeOptions = Array.from(new Set([card.userId || '', ...projectMemberIds].filter(Boolean)));
    const handleAssigneeChange = async (nextAssignee: string) => {
        if (nextAssignee === currentAssignee) return;
        try {
            await updateCard({ id: card.id, assigneeId: nextAssignee === card.userId ? '' : nextAssignee });
            onUpdate({ ...card, assigneeId: nextAssignee });
        } catch (error: any) {
            console.error('Failed to change assignee', error);
            alert(error.response?.data?.message || '담당자를 바꾸지 못했습니다.');
        }
    };

    // ✨ [신규] 선행 업무 클릭 핸들러
    const handlePredecessorClick = (pred: { id: string, taskCode?: string }) => {
        const targetId = pred.taskCode || pred.id;

        // 현재 모달을 닫고 이동하거나, URL을 변경하여 모달 내용을 갱신
        // 여기서는 URL 변경 방식을 사용 (AllTasksPage나 BoardPage에서 감지하여 갱신됨)
        if (location.pathname.includes('/tasks')) {
            navigate(`/tasks/card/${targetId}`);
        } else if (location.pathname.includes('/board')) {
            // 보드 페이지의 경우 outbox 여부 체크 등 경로가 복잡할 수 있음.
            // 안전하게 보드 ID가 있다면 해당 경로 유지
            navigate(location.pathname.replace(card.id, targetId));
        } else if (location.pathname.includes('/calendar')) {
            navigate(`/calendar/card/${targetId}`);
        } else {
            // 프로젝트 상세 등 기타 경로
            // 프로젝트 상세 페이지에서 모달을 띄운 경우라면, 단순히 데이터만 교체하는 게 나을 수도 있지만
            // 현재 구조상 URL 기반이 아닐 수 있으므로 (ProjectDetailPage는 selected state 사용)
            // ProjectDetailPage가 URL param을 보지 않는다면 로직 추가 필요.
            // 우선 URL 이동 시도:
            // 만약 ProjectDetailPage에서 모달을 띄웠다면 navigate로는 모달 갱신이 안될 수 있음 (state 기반이므로).
            // 하지만 이 컴포넌트는 범용이므로 navigate를 우선 사용.
            navigate(`../${targetId}`, { relative: "path" });
            // *참고: ProjectDetailPage에서는 setSelectedCard를 사용하므로,
            // 이 모달 내부에서 상위로 이벤트를 쏘아 올리거나 해야 완벽하지만,
            // 일단 Link 이동 효과를 내기 위해 아래 렌더링 부분 참고.
        }
    };

    const handleSaveCardDetails = useCallback(async (overrides?: { todoList?: ToDoDto[] }) => {
        if (readOnly) return; // ✨ readOnly면 저장 방지

        const trimmedTitle = title.trim();
        const formatDateForApi = (dateString: string) => {
            if (!dateString) return null;
            return dateString.replace('T', ' ') + ':00';
        };

        const currentApiTodos = overrides?.todoList || getApiTodoList(todos);
        const hasLabelsChanged = JSON.stringify(labels) !== JSON.stringify(initialLabels.current);
        const hasReportLevelsChanged = JSON.stringify(reportLevels) !== JSON.stringify(initialReportLevels.current);
        const hasStateChanged = cardState !== initialCardState.current;
        const hasTodosChanged = JSON.stringify(currentApiTodos) !== JSON.stringify(initialTodos.current);

        const hasChanged = trimmedTitle !== initialTitle.current || description !== initialDescription.current ||
            JSON.stringify(labels) !== JSON.stringify(initialLabels.current) ||
            JSON.stringify(reportLevels) !== JSON.stringify(initialReportLevels.current) ||
            startDate !== initialStartDate.current ||
            dueDate !== initialDueDate.current ||
            hasStateChanged ||
            hasTodosChanged;

        if (trimmedTitle && hasChanged) {
            try {
                const updatedData = {
                    ...card,
                    title: trimmedTitle,
                    description,
                    labels: hasLabelsChanged ? labels : null,
                    reportLevels: hasReportLevelsChanged ? reportLevels : null,
                    startDate: formatDateForApi(startDate),
                    dueDate: formatDateForApi(dueDate),
                    state: cardState,
                    status: null,
                    todoList: currentApiTodos,
                    // 담당자는 고르기 칸(handleAssigneeChange)으로만 바꾼다 — 열어 둔 사이 바뀐 값을 옛 값으로 되돌리지 않게 보내지 않는다
                    assigneeId: undefined,
                };
                const dataToSend = !isCardOwner
                    // 담당자는 진행 칸만 보낸다 (서버가 다른 칸을 바꾸려 하면 거절한다)
                    ? { id: card.id, state: cardState, todoList: currentApiTodos }
                    : {
                        ...updatedData,
                        attachments: (updatedData.attachments && updatedData.attachments.length === 0) ? null : updatedData.attachments,
                    };
                const response = await updateCard(dataToSend);
                initialTitle.current = trimmedTitle;
                initialDescription.current = description;
                initialCardState.current = cardState;
                initialTodos.current = currentApiTodos;
                return response.data;
            } catch (error) { console.error('Failed to update card', error); }
        } else if (!trimmedTitle) {
            setTitle(initialTitle.current);
        }
    }, [readOnly, isCardOwner, title, description, labels, reportLevels, startDate, dueDate, cardState, todos, card]);

    const handleClose = async () => {
        const updatedCard = await handleSaveCardDetails();
        onUpdate(updatedCard || card);
        if (!showWarning) onClose();
    };

    const handleDeleteCard = () => {
        if (contentLocked) return;
        if (window.confirm('Are you sure you want to delete this card? This action cannot be undone.')) {
            onDelete(card.id);
        }
    };

    const handleSaveComment = async () => {
        if (readOnly) return;
        if (editingComment) {
            const content = editingComment.content.trim();
            if (!content) return;
            await updateComment({ id: editingComment.id, content });
        } else {
            const content = newComment.trim();
            if (!content) return;
            await createComment({ cardId: card.id, content });
        }
        setNewComment('');
        setEditingComment(null);
        fetchComments();
    };

    const handleDeleteComment = async (commentId: string) => {
        if (readOnly) return;
        if (window.confirm('Delete this comment permanently?')) {
            await deleteComment(commentId);
            fetchComments();
        }
    };

    const refreshCardDetails = useCallback(async () => {
        try {
            const response = await getCardById(card.id);
            onUpdate(response.data);
        } catch (error) { console.error('Failed to refresh card details', error); }
    }, [card.id, onUpdate]);

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (contentLocked) return;
        const file = event.target.files?.[0];
        if (file) {
            try {
                await uploadAttachment(card.id, file);
                await refreshCardDetails();
            } catch (error) {
                console.error('Failed to upload attachment', error);
                alert('File upload failed.');
            }
        }
    };

    const handleDeleteAttachment = async (attachmentId: string) => {
        if (contentLocked) return;
        if (window.confirm('Delete this attachment permanently?')) {
            try {
                await deleteAttachment(card.id, attachmentId);
                await refreshCardDetails();
            } catch (error) {
                console.error('Failed to delete attachment', error);
                alert('Failed to delete attachment.');
            }
        }
    };

    const handleEditorImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        if (contentLocked) return;
        const file = event.target.files?.[0];
        if (file && editor) {
            try {
                const response = await uploadAttachment(card.id, file);
                const newAttachment = response.data;
                const imageUrl = `${baseURL}/card/${card.id}/attachment/${newAttachment.attachmentId}`;
                editor.chain().focus().setImage({ src: imageUrl }).run();
                await refreshCardDetails();
            } catch (error) {
                console.error('Failed to upload and insert image', error);
                alert('Image upload failed.');
            }
        }
    };

    const handleGoToCalendar = () => {
        if (card.startDate) {
            const dateOnly = card.startDate.split(' ')[0];
            navigate(`/calendar?date=${dateOnly}&cardId=${card.id}`);
        } else {
            navigate('/calendar');
        }
    };

    const handleGoToTasks = () => {
        navigate(`/tasks/card/${card.id}`);
    };

    const handleCopyCard = async () => {
        if (contentLocked) return;
        if (!window.confirm('현재 카드를 복사하시겠습니까? (내용, 라벨, Todo 등이 복제됩니다)')) return;
        try {
            const newTitle = `[복사] ${title}`;
            const createResponse = await createCard({
                boardId: card.boardId,
                cardListId: card.cardListId,
                title: newTitle,
                priority: card.priority
            });
            const newCardId = createResponse.data.id;
            const formatDateForApi = (dateString: string) => dateString ? dateString.replace('T', ' ') + ':00' : null;
            const copyPayload = {
                id: newCardId,
                title: newTitle,
                description: description,
                labels: labels,
                reportLevels: reportLevels,
                startDate: formatDateForApi(startDate),
                dueDate: formatDateForApi(dueDate),
                state: 'IDLE',
                status: null,
                todoList: getApiTodoList(todos),
            };
            await updateCard(copyPayload);
            alert('카드가 복사되었습니다.');
            onUpdate(card);
            onClose();
        } catch (error) {
            console.error('Failed to copy card', error);
            alert('카드 복사에 실패했습니다.');
        }
    };

    // ✨ [신규] 자동 저장 로직 (Debounce)
    useEffect(() => {
        if (readOnly) return;

        // 제목이나 본문이 초기값과 다를 때만 타이머 작동
        const hasChanged = title.trim() !== initialTitle.current || description !== initialDescription.current;

        if (hasChanged) {
            const timer = setTimeout(async () => {
                setIsSaving(true);
                await handleSaveCardDetails();
                setIsSaving(false);
                console.log("Auto-saved successfully");
            }, 2000); // 2초 동안 입력이 없으면 자동 저장

            return () => clearTimeout(timer); // 다음 입력이 들어오면 이전 타이머 취소
        }
    }, [title, description, readOnly, handleSaveCardDetails]); // title 또는 description 변경 시 실행

    return (
        <>
            <Modal visible={true} onClose={handleClose}>
                <ModalContentWrapper>
                    <ModalTopHeader>
                        <HeaderTitle>
                            <ModalHeader>
                                <CompleteCheckbox
                                    className={card.completed ? 'completed' : ''}
                                    onClick={handleSafeToggleComplete} // ✨ 교체
                                    style={{ cursor: readOnly ? 'default' : 'pointer', opacity: readOnly ? 0.7 : 1 }}
                                >
                                    {card.completed && <CheckIcon viewBox="0 0 24 24"><path fill="none" d="M20 6L9 17l-5-5"/></CheckIcon>}
                                </CompleteCheckbox>

                                {contentLocked ? (
                                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', padding: '0.25rem', color: 'var(--text-bright)', flexGrow: 1 }}>
                                        {title}
                                    </div>
                                ) : (
                                    <CardTitleInput
                                        value={title}
                                        onChange={(e) => {
                                            setTitle(e.target.value);
                                            e.target.style.height = 'auto';
                                            e.target.style.height = e.target.scrollHeight + 'px';
                                        }}
                                        onBlur={() => handleSaveCardDetails()}
                                        style={{ textDecoration: card.completed ? 'line-through' : 'none', height: 'auto' }}
                                        rows={1}
                                    />
                                )}
                            </ModalHeader>
                        </HeaderTitle>
                        <HeaderButtonContainer>
                            {!readOnly && (
                                <StartWorkButton
                                    onClick={handleToggleWorkStatus}
                                    isWorking={card.status === 'IN_PROGRESS'}
                                >
                                    {card.status === 'IN_PROGRESS' ? 'Stop' : 'Start'}
                                </StartWorkButton>
                            )}
                            <CommentToggleButton onClick={() => setShowCommentsOnMobile(!showCommentsOnMobile)}>
                                {showCommentsOnMobile ? (
                                    "<-"
                                ) : (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>
                                )}
                            </CommentToggleButton>
                            <HeaderCloseButton onClick={handleClose}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </HeaderCloseButton>
                        </HeaderButtonContainer>
                    </ModalTopHeader>
                    <ModalBody>
                        <ModalGrid showComments={showCommentsOnMobile}>
                            <MainContent>
                                <TabHeader>
                                    <TabButton isActive={activeTab === 'general'} onClick={() => handleTabChange('general')}>General</TabButton>
                                    <TabButton isActive={activeTab === 'todo'} onClick={() => handleTabChange('todo')}>To-Do List</TabButton>
                                    <TabButton isActive={activeTab === 'desc'} onClick={() => handleTabChange('desc')}>Description</TabButton>
                                </TabHeader>

                                {activeTab === 'general' && (
                                    <>
                                        <MetadataContainer>
                                            <MetadataGroup>
                                                <DateGroup>
                                                    <DateWrapper>
                                                        <DateLabel>Start Date</DateLabel>
                                                        <DateInput type="datetime-local" value={startDate} disabled={contentLocked} onChange={e => setStartDate(e.target.value)} onBlur={() => handleSaveCardDetails()} />
                                                    </DateWrapper>
                                                    <Tilde>~</Tilde>
                                                    <DateWrapper>
                                                        <DateLabel>Due Date</DateLabel>
                                                        <DateInput type="datetime-local" value={dueDate} disabled={contentLocked} onChange={e => setDueDate(e.target.value)} onBlur={() => handleSaveCardDetails()} />
                                                    </DateWrapper>
                                                </DateGroup>
                                            </MetadataGroup>

                                            <MetadataGroup>
                                                <TimeInfoGroup>
                                                    <TimeInfoRow>
                                                        <TimeLabel>프로젝트</TimeLabel>
                                                        <TimeValue>{projectName || '-'}</TimeValue>
                                                    </TimeInfoRow>
                                                    <TimeInfoRow>
                                                        <TimeLabel>담당</TimeLabel>
                                                        <TimeValue>
                                                            {card.projectId && isCardOwner && !readOnly && assigneeOptions.length > 1 ? (
                                                                <StateSelect
                                                                    value={currentAssignee}
                                                                    onChange={(e) => handleAssigneeChange(e.target.value)}
                                                                    bgColor="var(--text-bright)"
                                                                    aria-label="담당자"
                                                                >
                                                                    {assigneeOptions.map(id => (
                                                                        <option key={id} value={id}>
                                                                            {id === card.userId ? `${id} (만든 사람)` : id}
                                                                        </option>
                                                                    ))}
                                                                </StateSelect>
                                                            ) : (
                                                                <span>
                                                                    {currentAssignee || '-'}
                                                                    {currentAssignee === user?.id && !isCardOwner ? ' (나)' : ''}
                                                                </span>
                                                            )}
                                                        </TimeValue>
                                                    </TimeInfoRow>
                                                    <TimeInfoRow>
                                                        <TimeLabel>선행 업무</TimeLabel>
                                                        <TimeValue>
                                                            {predecessors.length > 0 ? (
                                                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                                                    {predecessors.map(pred => (
                                                                        <span
                                                                            key={pred.id}
                                                                            onClick={() => handlePredecessorClick(pred)}
                                                                            style={{
                                                                                cursor: 'pointer',
                                                                                textDecoration: 'underline',
                                                                                color: 'var(--primary-accent)'
                                                                            }}
                                                                        >
                                                                [{pred.taskCode}]{pred.title}
                                                            </span>
                                                                    ))}
                                                                </div>
                                                            ) : '없음'}
                                                        </TimeValue>
                                                    </TimeInfoRow>
                                                </TimeInfoGroup>
                                            </MetadataGroup>

                                            <MetadataGroup>
                                                <TimeInfoGroup>
                                                    <TimeInfoRow>
                                                        <TimeLabel>상태</TimeLabel>
                                                        <TimeValue>
                                                            <StateSelect
                                                                value={cardState}
                                                                onChange={(e) => setCardState(e.target.value)}
                                                                onBlur={() => handleSaveCardDetails()}
                                                                disabled={readOnly}
                                                                bgColor={getStateColor(cardState)}
                                                            >
                                                                <option value="IDLE">대기</option>
                                                                <option value="FOLLOWUP">팔로업</option>
                                                                <option value="PROCESS">진행중</option>
                                                                <option value="COMPLETED">완료</option>
                                                            </StateSelect>
                                                        </TimeValue>
                                                    </TimeInfoRow>
                                                    <TimeInfoRow>
                                                        <TimeLabel>업무 진행 시간</TimeLabel>
                                                        <TimeValue>{formatMs(card.inputTime)}</TimeValue>
                                                    </TimeInfoRow>
                                                    <TimeInfoRow>
                                                        <TimeLabel>실제 업무 시간</TimeLabel>
                                                        <TimeValue>
                                                            {card.realStartTime ? formatDateTime(card.realStartTime) : '-'} ~ {card.realEndTime ? formatDateTime(card.realEndTime) : ''}
                                                        </TimeValue>
                                                    </TimeInfoRow>
                                                </TimeInfoGroup>
                                            </MetadataGroup>

                                            <MetadataGroup>
                                                <LabelGrid>
                                                    {labels.map(label => (
                                                        <LabelPill key={label.name} color={label.color}>
                                                            {label.name}
                                                            {!contentLocked && <SmallDeleteButton onClick={() => handleDeleteLabel(label.name)}>×</SmallDeleteButton>}
                                                        </LabelPill>
                                                    ))}
                                                </LabelGrid>
                                                {!contentLocked && (
                                                    <AddItemForm>
                                                        <Input placeholder="New label name..." value={newLabelName} onChange={e => setNewLabelName(e.target.value)} />
                                                        <ColorInput type="color" value={newLabelColor} onChange={e => setNewLabelColor(e.target.value)} />
                                                        <Button onClick={handleAddLabel}>Add</Button>
                                                    </AddItemForm>
                                                )}
                                            </MetadataGroup>

                                            <MetadataGroup>
                                                <ReportLevelLine>
                                                    {reportLevels.map((level, index) => (
                                                        <React.Fragment key={level.name}>
                                                            <ReportLevelPill>
                                                                <span>{level.name}</span>
                                                                {!contentLocked && <SmallDeleteButton onClick={() => handleDeleteReportLevel(level.name)}>×</SmallDeleteButton>}
                                                            </ReportLevelPill>
                                                            {index < reportLevels.length - 1 && <ArrowSpan>→</ArrowSpan>}
                                                        </React.Fragment>
                                                    ))}
                                                </ReportLevelLine>
                                                {!contentLocked && (
                                                    <AddItemForm>
                                                        <Input placeholder="New report level..." value={newReportLevelName} onChange={e => setNewReportLevelName(e.target.value)} />
                                                        <Button onClick={handleAddReportLevel}>Add</Button>
                                                    </AddItemForm>
                                                )}
                                            </MetadataGroup>
                                        </MetadataContainer>

                                        <AttachmentSection>
                                            {!contentLocked && (
                                                <>
                                                    <AddAttachmentButton onClick={() => fileUploadRef.current?.click()}>Add an attachment</AddAttachmentButton>
                                                    <input type="file" ref={fileUploadRef} onChange={handleFileUpload} style={{ display: 'none' }} />
                                                    <input type="file" ref={imageUploadRef} onChange={handleEditorImageUpload} style={{ display: 'none' }} accept="image/*" />
                                                </>
                                            )}
                                            <AttachmentList>
                                                {card.attachments?.map(att => (
                                                    <AttachmentItem key={att.attachmentId}>
                                                        <AttachmentName onClick={() => downloadAttachment(card.id, att.attachmentId, att.originalFilename)}>
                                                            {att.originalFilename}
                                                        </AttachmentName>
                                                        {!contentLocked && (
                                                            <AttachmentActions>
                                                                <button onClick={() => handleDeleteAttachment(att.attachmentId)}>Delete</button>
                                                            </AttachmentActions>
                                                        )}
                                                    </AttachmentItem>
                                                ))}
                                            </AttachmentList>
                                        </AttachmentSection>
                                    </>
                                )}

                                {activeTab === 'todo' && (
                                    <TodoSection>
                                        {todos.length === 0 && !readOnly ? (
                                            <EmptyTodoPlaceholder onClick={handleAddTodo} style={{ marginTop: '1rem' }}>
                                                + 할 일 목록 만들기
                                            </EmptyTodoPlaceholder>
                                        ) : (
                                            <TodoTableContainer style={{ marginTop: '0.5rem' }}>
                                                {todos.map((todo, index) => (
                                                    <TodoRowItem
                                                        key={todo.id}
                                                        className="todo-row-item"
                                                        data-index={index}
                                                        draggable={!readOnly}
                                                        // onDragStart={(e) => {
                                                        //     if (readOnly) return;
                                                        //     dragItem.current = index;
                                                        //     e.currentTarget.classList.add('dragging');
                                                        // }}
                                                        onDragEnter={(e) => {
                                                            if (readOnly) return;
                                                            dragOverItem.current = index;
                                                        }}
                                                        // onDragEnd={(e) => {
                                                        //     if (readOnly) return;
                                                        //     e.currentTarget.classList.remove('dragging');
                                                        //     handleSort();
                                                        // }}
                                                        onDragOver={(e) => e.preventDefault()}
                                                        // 모바일 터치 이벤트 추가
                                                        // onTouchStart={() => handleTouchStart(index)}
                                                        // onTouchMove={handleTouchMove}
                                                        // onTouchEnd={handleTouchEnd}
                                                    >
                                                        {!readOnly && (
                                                            <DragHandle
                                                                // ✨ [수정] 드래그와 터치 시작 트리거를 핸들로 모두 이동
                                                                draggable={true}
                                                                onDragStart={(e) => {
                                                                    dragItem.current = index;
                                                                    // 드래그 시 시각 효과를 위해 부모 Row에 클래스 추가
                                                                    e.currentTarget.closest('.todo-row-item')?.classList.add('dragging');
                                                                }}
                                                                onDragEnd={(e) => {
                                                                    e.currentTarget.closest('.todo-row-item')?.classList.remove('dragging');
                                                                    handleSort();
                                                                }}
                                                                // ✨ [핵심] 터치 이벤트는 오직 핸들에서만 시작되도록 함
                                                                onTouchStart={() => handleTouchStart(index)}
                                                                onTouchMove={handleTouchMove}
                                                                onTouchEnd={handleTouchEnd}
                                                            >
                                                                <svg viewBox="0 0 24 24" fill="currentColor">
                                                                    <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                                                                </svg>
                                                            </DragHandle>
                                                        )}
                                                        <TodoCheckbox
                                                            checked={todo.completed}
                                                            readOnly={readOnly}
                                                            onClick={() => !readOnly && handleToggleTodo(todo.id)}
                                                        >
                                                            {todo.completed && <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                                                        </TodoCheckbox>
                                                        <TodoInput
                                                            value={todo.content}
                                                            onChange={(e) => handleChangeTodo(todo.id, e.target.value)}
                                                            onBlur={handleBlurTodo}
                                                            readOnly={readOnly}
                                                            placeholder={readOnly ? "" : "할 일을 입력하세요..."}
                                                            completed={todo.completed}
                                                        />
                                                        {!readOnly && (
                                                            <TodoDeleteBtn onClick={() => handleDeleteTodo(todo.id)}>
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                                            </TodoDeleteBtn>
                                                        )}
                                                    </TodoRowItem>
                                                ))}
                                                {!readOnly && (
                                                    <AddTodoRowButton onClick={handleAddTodo}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                                                        항목 추가
                                                    </AddTodoRowButton>
                                                )}
                                            </TodoTableContainer>
                                        )}
                                    </TodoSection>
                                )}

                                {activeTab === 'desc' && (
                                    <EditorContainer ref={editorContentRef} style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', flex: 1 }}>
                                        <TiptapWrapper
                                            isExpanded={true}
                                            isEditable={!contentLocked && isDescriptionEditable}
                                            onClick={handleDescriptionClick}
                                            style={{ flex: 1, cursor: contentLocked ? 'default' : 'text' }}
                                        >
                                            {!contentLocked && <MenuBar editor={editor} onImageUpload={() => imageUploadRef.current?.click()} />}
                                            <EditorContent editor={editor} onBlur={() => handleSaveCardDetails()} />
                                        </TiptapWrapper>
                                    </EditorContainer>
                                )}
                            </MainContent>
                            <SideContent>
                                <CommentList>
                                    {comments.map(comment => (
                                        <CommentItem key={comment.id}>
                                            <CommentHeader>
                                                <CommentAuthor>{comment.userId}</CommentAuthor>
                                                <span>{new Date(comment.createDateTime).toLocaleString()}</span>
                                            </CommentHeader>
                                            {editingComment?.id === comment.id ? (
                                                <Input value={editingComment.content} onChange={(e) => setEditingComment({ ...editingComment, content: e.target.value })} onBlur={handleSaveComment} autoFocus/>
                                            ) : (
                                                <CommentContent dangerouslySetInnerHTML={{ __html: comment.content }} />
                                            )}
                                            {user?.id === comment.userId && !editingComment && !readOnlyProp && (
                                                <CommentActions>
                                                    <button onClick={() => setEditingComment(comment)}>Edit</button>
                                                    <button onClick={() => handleDeleteComment(comment.id)}>Delete</button>
                                                </CommentActions>
                                            )}
                                        </CommentItem>
                                    ))}
                                </CommentList>
                                {!readOnlyProp && (
                                    <>
                                        <CommentTextarea placeholder="Write a comment..." value={newComment} onChange={(e) => setNewComment(e.target.value)}/>
                                        <Button onClick={handleSaveComment} style={{ alignSelf: 'flex-start', marginTop: '0.5rem' }}>Save Comment</Button>
                                    </>
                                )}
                            </SideContent>
                        </ModalGrid>
                    </ModalBody>
                    <ButtonGroup readOnly={readOnly}>
                        {isSaving && <span style={{ marginRight: 'auto', fontSize: '0.8rem', color: 'var(--primary-accent)' }}>저장 중...</span>}

                        {fromCalendar && (
                            <GoToBoardContainer>
                                <Button onClick={handleGoToTasks}>→ Go to Tasks</Button>
                            </GoToBoardContainer>
                        )}
                        {fromBoard && card.startDate && (
                            <GoToBoardContainer>
                                <Button onClick={handleGoToCalendar}>→ Go to Calendar</Button>
                            </GoToBoardContainer>
                        )}
                        {onRemoveFromProject && (card.userId === user?.id || isAdminEdit) && (
                            <Button
                                onClick={() => {
                                    if(window.confirm('이 업무를 프로젝트에서 제외하시겠습니까?')) {
                                        onRemoveFromProject();
                                    }
                                }}
                                style={{ backgroundColor: '#ff9800', color: 'white' }}
                            >
                                프로젝트에서 제외
                            </Button>
                        )}
                        {!readOnly && (
                            <>
                                {/* ✨ [신규] 수동 저장 버튼 추가 */}
                                <Button
                                    onClick={handleManualSave}
                                    cyan
                                    disabled={isSaving}
                                >
                                    {isSaving ? '저장 중...' : '저장'}
                                </Button>
                                {!contentLocked && <CopyButton onClick={handleCopyCard}>Copy Card</CopyButton>}
                                {!contentLocked && <DeleteButton onClick={handleDeleteCard}>Delete Card</DeleteButton>}
                            </>
                        )}
                        <Button onClick={handleClose}>Close</Button>
                    </ButtonGroup>
                </ModalContentWrapper>
            </Modal>

            {showWarning && (
                <WarningModal
                    cards={inProgressCards}
                    onClose={() => {
                        setShowWarning(false);
                        pendingStatusUpdate.current = null;
                    }}
                    onConfirm={async () => {
                        if (pendingStatusUpdate.current) {
                            await updateCardStatus(pendingStatusUpdate.current);
                        }
                        setShowWarning(false);
                    }}
                    currentCardId={card.id}
                />
            )}
        </>
    );
};

export default CardDetailModal;